import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Sparkles, ArrowUpRight } from "lucide-react";
import api from "../lib/api";

const QUARTER_MONTHS = { 1: "Jan – Mar", 2: "Apr – Jun", 3: "Jul – Sep", 4: "Oct – Dec" };

/** Board Books — every quarter you've held sessions in, compiled or not. */
export default function BoardBooks() {
  const [quarters, setQuarters] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/quarter-reviews").then((r) => setQuarters(r.data.quarters || [])).catch(() => {}),
      api.get("/year-reviews").then((r) => setYears(r.data.years || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="board-books-page">
      <div>
        <p className="label-eyebrow">Artefacts</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2">Board Books</h1>
        <p className="text-[#8a8a8a] mt-3 max-w-xl">
          Every year and quarter you held sessions in, ready to be rolled into a single review you can print, keep or share.
        </p>
        <Link
          to="/milestones"
          className="inline-flex items-center gap-1.5 min-h-[40px] mt-2 text-sm text-[#c89f45] hover:underline"
          data-testid="board-books-milestones-link"
        >
          See your milestone wall <ArrowUpRight size={14} />
        </Link>
      </div>

      {loading ? (
        <p className="text-[#8a8a8a]">Loading…</p>
      ) : quarters.length === 0 ? (
        <div className="surface rounded-md p-8 text-center" data-testid="board-books-empty">
          <BookOpen size={22} className="mx-auto text-[#c89f45] mb-3" />
          <p className="text-[#eaeaea]">No completed sessions yet.</p>
          <p className="text-sm text-[#8a8a8a] mt-1">
            Hold your first session and this shelf starts filling itself.
          </p>
          <Link to="/meetings" className="inline-block mt-4 text-sm text-[#c89f45] hover:underline">
            Go to sessions →
          </Link>
        </div>
      ) : (
        <>
        {years.length > 0 && (
          <ul className="space-y-3 mb-8">
            {years.map((y) => (
              <li key={`year-${y.year}`}>
                <Link
                  to={`/year-review/${y.year}`}
                  className="surface rounded-md p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 border-[#c89f45]/25 hover:border-[#c89f45]/50 transition-colors group"
                  data-testid={`board-book-year-${y.year}`}
                >
                  <div>
                    <p className="label-eyebrow">
                      Annual review · {y.sessions_held} session{y.sessions_held === 1 ? "" : "s"}
                    </p>
                    <h2 className="font-serif text-xl mt-1 group-hover:text-[#c89f45] transition-colors">
                      {y.year} — the year in the chair
                    </h2>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 justify-between sm:justify-end">
                    {y.compiled_at ? (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#c89f45] border border-[#c89f45]/40 rounded-full px-2.5 py-1">
                        Compiled
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a] border border-[#2a2a2a] rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                        <Sparkles size={9} /> Ready to compile
                      </span>
                    )}
                    <ArrowUpRight size={16} className="text-[#8a8a8a] group-hover:text-[#c89f45] transition-colors" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <ul className="space-y-3">
          {quarters.map((q) => (
            <li key={`${q.year}-${q.quarter}`}>
              <Link
                to={`/quarter-review/${q.year}/${q.quarter}`}
                className="surface rounded-md p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 hover:border-[#c89f45]/40 transition-colors group"
                data-testid={`board-book-${q.year}-q${q.quarter}`}
              >
                <div>
                  <p className="label-eyebrow">
                    {QUARTER_MONTHS[q.quarter]} · {q.sessions_held} session{q.sessions_held === 1 ? "" : "s"}
                  </p>
                  <h2 className="font-serif text-xl mt-1 group-hover:text-[#c89f45] transition-colors">
                    Q{q.quarter} {q.year}
                  </h2>
                </div>
                <div className="flex items-center gap-3 shrink-0 justify-between sm:justify-end">
                  {q.compiled_at ? (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#c89f45] border border-[#c89f45]/40 rounded-full px-2.5 py-1">
                      Compiled
                    </span>
                  ) : (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a] border border-[#2a2a2a] rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                      <Sparkles size={9} /> Ready to compile
                    </span>
                  )}
                  <ArrowUpRight size={16} className="text-[#8a8a8a] group-hover:text-[#c89f45] transition-colors" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
        </>
      )}
    </div>
  );
}
